/**
 * Type Definitions
 */

/**
 * @typedef {Object} RaffleTranslation
 * @property {string} lang Language mutation
 * @property {string} title Title in given mutation
 * @property {string} slug Url slug for given mutation
 * @property {string} description Description for given mutation
 */

/**
 * @typedef {Object} RafflePrice
 * @property {string} currency Currency code
 * @property {number} value Value in given currency
 */

/**
 * @typedef {Object} RaffleSizeSet
 * @property {string} name Unique identifier
 * @property {RaffleSize[]} sizes Sizes
 */

/**
 * @typedef {Object} RaffleSize
 * @property {string} id Unique identifier
 * @property {string} us Size in US metrics
 * @property {string} eur Size in EUR metrics
 * @property {string} uk Size in UK metrics
 * @property {string} cm Size in centimeters
 * @property {number} pieces Amount available
 */

/**
 * @class Raffle
 * @property {string} id Raffle id
 * @property {Object.<string, RaffleTranslation>} translations Raffle translations
 * @property {Object.<string, RafflePrice>} prices Raffle prices
 * @property {Object.<string, RaffleSizeSet>} sizeSets Raffle size sets
 * @property {string[]} images Images
 * @property {string} createdAt Created at
 * @property {string} publishAt Published at
 * @property {string} openRegistrationAt When will the registration open
 * @property {string} closeRegistrationAt When will the registration close
 * @property {string} status Status
 * @property {?string} footshopLink Footshop Link
 * @property {boolean} finished Is raffle finished?
 * @this Raffle
 */
export default class Raffle {

	static IMAGE_TYPE_LANDSCAPE = 'landscape';
	static IMAGE_TYPE_PORTRAIT ='portrait';
	static IMAGE_TYPE_DEFAULT = 'landscape';

	/**
	 * @return {Raffle}
	 */
	static normalize(data) {
		return Object.assign(new Raffle(), data);
	}

	/**
	 * @param {string} currencyCode
	 */
	getPrice(currencyCode) {
		if (this.prices[currencyCode]) {
			return this.prices[currencyCode].value;
		}
		return 0;
	}

	/**
	 * @returns {?string}
	 * @param {string} mutation
	 */
	getTitle(mutation) {
		return this.getTranslationField(mutation, 'title');
	}

	/**
	 * @returns {?string}
	 * @param {string} mutation
	 */
	getSubtitle(mutation) {
		return this.getTranslationField(mutation, 'subtitle');
	}

	/**
	 * @returns {?string}
	 * @param {string} mutation
	 */
	getDescription(mutation) {
		return this.getTranslationField(mutation, 'description');
	}

	/**
	 * @returns {?string}
	 * @param {string} mutation
	 */
	getSlug(mutation) {
		return this.getTranslationField(mutation, 'slug');
	}

	/**
	 * @returns {?Object}
	 * @param {string} mutation
	 */
	getTranslation(mutation) {
		if (this.translations[mutation]) {
			return this.translations[mutation];
		}

		for (let key in this.translations) {
			if (this.translations.hasOwnProperty(key)) {
				return this.translations[key];
			}
		}
	}

	/**
	 * @returns {?string}
	 * @param {string} mutation
	 * @param {string} field
	 */
	getTranslationField(mutation, field) {
		const translation = this.getTranslation(mutation);
		return translation ? translation[field] : null;
	}

	/**
	 * Is Raffle Active? (openRegistrationAt < now < closeRegistrationAt)
	 *
	 * @return {boolean}
	 */
	isActive() {
		const now = new Date();
		return (
			now > new Date(this.openRegistrationAt) &&
			now < new Date(this.closeRegistrationAt)
		);
	}

	/**
	 * Is Raffle Coming Soon? (publishAt < now < openRegistrationAt)
	 *
	 * @return {boolean}
	 */
	isComingSoon() {
		const now = new Date();
		return (
			now > new Date(this.publishAt) &&
			now < new Date(this.openRegistrationAt)
		);
	}

	/**
	 * Is Raffle Closing Soon? (active && (now +24h) > closeRegistrationAt)
	 *
	 * @return {boolean}
	 */
	isClosingSoon() {
		const tomorrow = new Date();
		tomorrow.setDate(tomorrow.getDate()+1);
		return (
			this.isActive() &&
			tomorrow > new Date(this.closeRegistrationAt)
		);
	}

	/**
	 * Is Raffle Closed? (today > closeRegistrationAt)
	 *
	 * @return {boolean}
	 */
	isClosed() {
		const today = new Date();
		return (
			today > new Date(this.closeRegistrationAt)
		);
	}

	/**
	 * Has raffle footshop link filled in?
	 *
	 * @return {boolean}
	 */
	hasFootshopLink() {
		return this.footshopLink && this.footshopLink.length > 0;
	}

	/**
	 * Should we show a footshop link? (used on raffle detail)
	 *
	 * @return {boolean}
	 */
	showLinkToFootshop() {
		return this.hasFootshopLink() && this.isClosed();
	}

	/**
	 * @return {Date}
	 */
	getCreatedAtAsDate() {
		return new Date(this.createdAt);
	}

	/**
	 * @return {Date}
	 */
	getPublishAtAsDate() {
		return new Date(this.publishAt);
	}

	/**
	 * @return {Date}
	 */
	getOpenRegistrationAtAsDate() {
		return new Date(this.openRegistrationAt);
	}

	/**
	 * @return {Date}
	 */
	getCloseRegistrationAtAsDate() {
		return new Date(this.closeRegistrationAt);
	}

	/**
	 * @return {Date}
	 */
	getResultsAtAsDate() {
		return new Date(this.resultAt);
	}

	/**
	 * @return {string[]}
	 */
	getAvailableSexes() {
		if (!this.sizeSets) {
			return [];
		}

		return Object.keys(this.sizeSets).filter(sex => this.sizeSets[sex].sizes && !!this.sizeSets[sex].sizes.length);
	}

	/**
	 * @return {?string}
	 */
	getDefaultSex() {
		const availableSexes = this.getAvailableSexes();
		if (availableSexes.length > 0) {
			return availableSexes[0];
		}
		return null;
	}

	/**
	 * @return {RaffleSize[]}
	 */
	getSizeSetSizes(sizeSet) {
		if (this.sizeSets.hasOwnProperty(sizeSet)) {
			return this.sizeSets[sizeSet].sizes;
		}
		return [];
	}

	/**
	 * @param {string} sizerunId
	 *
	 * @return {?RaffleSize}
	 */
	getSize(sizerunId) {
		return this.getBySizerunId(sizerunId);
	}

	/**
	 * @param {string} sizerunId
	 *
	 * @return {?RaffleSize}
	 */
	getSizeSet(sizerunId) {
		return this.getBySizerunId(sizerunId, 'SizeSet');
	}

	getBySizerunId(sizerunId, type) {
		for (let sizeSet in this.sizeSets) {
			if (this.sizeSets.hasOwnProperty(sizeSet)) {
				let sizeSetSizes = this.sizeSets[sizeSet].sizes;
				for (let size in sizeSetSizes) {
					if (sizeSetSizes.hasOwnProperty(size)) {
						let sizeSetSize = sizeSetSizes[size];
						if (sizeSetSize.id === sizerunId) {
							switch (type) {
								case 'SizeSet':
									return this.sizeSets[sizeSet];

								default:
									return sizeSetSize;
							}
						}
					}
				}
			}
		}
		return null;
	}

	/**
	 * Get main image
	 *
	 * @return {?string}
	 */
	getMainImage(type = Raffle.IMAGE_TYPE_DEFAULT) {
		const images = this[this.getImageProperty(type)];
		return images.length > 0 ? images[0] : null;
	}

	/**
	 *
	 * @param {string} type
	 * @return {string}
	 */
	getImageProperty(type) {
		switch (type) {
			case Raffle.IMAGE_TYPE_PORTRAIT:
				return 'imagesPortrait';
			case Raffle.IMAGE_TYPE_LANDSCAPE:
			case Raffle.IMAGE_TYPE_DEFAULT:
				return 'images';
			default:
				throw new Error(`Invalid image type: "${type}"`);
		}
	}

	/**
	 * Get brand
	 *
	 * @return {?string}
	 */
	get brand() {
		return '';
	}
}



// WEBPACK FOOTER //
// ./models/Raffle.js